import { Box, Button, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';

import { listScrollGradient } from 'utils/styles.utils';

import { useModalControl } from 'hooks/useModalControl';
import {
  useAccount,
  useMarkUnsupportedDevice,
  useSignOut,
} from 'restful/accounts';

import Authentication from 'components/@account/Authentication';
import AccountCard from 'components/@transfer/AccountCard';

interface Props {
  redirectUrl: string;
  onSuccess: () => void;
}

const Footer = ({ redirectUrl, onSuccess }: Props) => {
  const intl = useIntl();
  const { open, onClose, onOpen } = useModalControl();
  const { isSignedIn } = useAccount();
  const { enqueueSnackbar } = useSnackbar();
  const { signOut } = useSignOut();

  const { markUnsupportedDevice, isLoading } = useMarkUnsupportedDevice();

  const handleLogin = () => {
    onOpen();
  };

  const handleSignOut = () => {
    signOut({ redirectUrl });
  };

  const handleMarkUnsupportedDevice = () => {
    markUnsupportedDevice(undefined, {
      onSuccess: () => {
        onSuccess();
      },
      onError: (error) => {
        Sentry.captureException(error);
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'unsupported_device.accept.error.alert' },
            { variant: 'error' },
          ),
        );
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          display: 'block',
          position: { xs: 'sticky', md: 'inherit' },
          bottom: { xs: 0, md: 'auto' },
          mx: { xs: -2, md: 0 },
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 3,
            px: 2,
            borderRadius: { xs: 0, md: '16px' },
            border: { xs: 0, md: '1px solid' },
            borderTop: { xs: '1px solid', md: '1px solid' },
            borderColor: { xs: 'divider', md: 'divider' },
            ...listScrollGradient(),
          }}
        >
          {isSignedIn ? (
            <Stack
              sx={{
                gap: 3,
              }}
            >
              <AccountCard />
              <Button
                fullWidth
                color="secondary"
                variant="outlined"
                onClick={handleMarkUnsupportedDevice}
                loading={isLoading}
              >
                <FormattedMessage id="unsupported_device.request" />
              </Button>
              <Button
                variant="text"
                onClick={handleSignOut}
                disabled={isLoading}
                fullWidth
              >
                <FormattedMessage id="unsupported_device.switch_account" />
              </Button>
            </Stack>
          ) : (
            <>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  mb: 2,
                }}
              >
                <FormattedMessage id="unsupported_device.login.title" />
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleLogin}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                <FormattedMessage id="unsupported_device.login.label" />
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Authentication
        titleIntlId="unsupported_device.login.title"
        drawer={{
          open,
          onClose,
        }}
        afterSignInUrl={redirectUrl}
        originSourceCategory="UnsupportedDevice"
      />
    </>
  );
};

export default Footer;
